import { useMutation, useQueryClient, UseMutationResult } from "@tanstack/react-query";
import { apiFetch, ApiError } from "../api/client";
import { TRASH_WORKSPACES_KEY, trashReposKey } from "../api/hooks/useTrash";
import { WORKSPACES_KEY } from "../api/hooks/useWorkspaces";
import { reposQueryKey } from "../repos/useRepos";
import type { TrashEntry } from "../api/types";

// Restore / permanent-delete are addressed by aggregate ID: every slug lookup on
// the server is alive-filtered, so a tombstone can only be named by its id.
function entryPath(entry: TrashEntry, wsSlug: string): string {
  return entry.type === "workspace"
    ? `/api/me/trash/workspaces/${entry.id}`
    : `/api/ws/${wsSlug}/trash/repos/${entry.id}`;
}

function useInvalidateTrash(wsSlug: string): () => void {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: TRASH_WORKSPACES_KEY });
    qc.invalidateQueries({ queryKey: trashReposKey(wsSlug) });
    // A restore puts the aggregate back into the live lists.
    qc.invalidateQueries({ queryKey: WORKSPACES_KEY });
    qc.invalidateQueries({ queryKey: reposQueryKey(wsSlug) });
  };
}

export function useRestoreFromTrash(wsSlug: string): UseMutationResult<unknown, ApiError, TrashEntry> {
  const invalidate = useInvalidateTrash(wsSlug);
  return useMutation<unknown, ApiError, TrashEntry>({
    mutationFn: (entry) => apiFetch<unknown>(`${entryPath(entry, wsSlug)}/restore`, { method: "POST" }),
    onSuccess: invalidate,
  });
}

export function usePurgeFromTrash(wsSlug: string): UseMutationResult<void, ApiError, TrashEntry> {
  const invalidate = useInvalidateTrash(wsSlug);
  return useMutation<void, ApiError, TrashEntry>({
    mutationFn: (entry) => apiFetch<void>(entryPath(entry, wsSlug), { method: "DELETE" }),
    onSuccess: invalidate,
  });
}
